import { GET_SCHOOL_PROPERTIES_QUERY, useGetCategoriesQuery } from '@schooly/api';
import { createStatuses } from '@schooly/api';
import { ApiError } from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { Loading } from '@schooly/style';
import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { SchoolSettingsLayout } from '../../../SchoolSettingsLayout';
import { useSchoolTuneStatuses } from './hooks/useSchoolTuneStatuses';
import { SchoolTuneStatusesForm } from './scheme';
import { SchoolTuneStatusesModalContent } from './SchoolTuneStatusesModalContent';
import { prepareStatusesToSave } from './utils';

export const SchoolTuneStatusesModal: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useNotifications();
  const { schoolId, currentSchool, formStudentStatuses, formStaffStatuses, isLoading } =
    useSchoolTuneStatuses();
  const [saving, setSaving] = useState(false);

  const { data: categoriesData } = useGetCategoriesQuery();
  const categories = categoriesData?.categories;

  const handleClose = useCallback(() => {
    if (saving) {
      return;
    }

    navigate({ pathname: '/settings/tune', search: '?tab=statuses' });
  }, [navigate, saving]);

  const handleSubmit = useCallback<SubmitHandler<SchoolTuneStatusesForm>>(
    async (data) => {
      if (!schoolId || !categories) {
        return;
      }

      setSaving(true);

      const statuses = prepareStatusesToSave({
        categories,
        data,
        predicate: (categoryId, index) => (status, i) => ({
          name: status.name ?? '',
          category_id: categoryId,
          order: index + i,
        }),
      });

      try {
        await createStatuses(schoolId, statuses);

        queryClient.invalidateQueries([GET_SCHOOL_PROPERTIES_QUERY]);

        navigate({ pathname: '/settings/tune', search: '?tab=statuses' });
      } catch (err) {
        console.error(err);
        showError(err as ApiError);
      }

      setSaving(false);
    },
    [categories, navigate, queryClient, schoolId, showError],
  );

  if (isLoading) {
    return <Loading />;
  }

  return (
    <SchoolSettingsLayout>
      <SchoolTuneStatusesModalContent
        title={currentSchool?.name || ''}
        isLoading={!categoriesData}
        isSaving={saving}
        onSubmit={handleSubmit}
        onClose={handleClose}
        studentStatuses={formStudentStatuses}
        staffStatuses={formStaffStatuses}
        categories={categories ?? []}
      />
    </SchoolSettingsLayout>
  );
};
